import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import VisualizeResultsTable from "./visualize-results-table";
import { useRecoilState, useRecoilValue } from "recoil";
import { getResults, getStatus, icn3dUrlState, resultsState, statusState, isDone, paramsSelector } from "./visualize.state";
import { withInterval } from "./visualize-form.utils";

export default function VisualizeResults() {
  const { id } = useParams();
  const params = useRecoilValue(paramsSelector(id));
  const [status, setStatus] = useRecoilState(statusState);
  const [results, setResults] = useRecoilState(resultsState);

  const refreshState = useCallback(async (id) => {
    const status = await getStatus(id);
    setStatus(status);

    if (status && isDone(status.status)) {
      setResults(await getResults(id));
    }
  }, [setStatus, setResults]);

  useEffect(() => {
    if (id && (!results?.length)) {
      const cleanup = withInterval(() => refreshState(id), 1000 * 5);
      if (isDone(status?.status)) cleanup();
      return cleanup;
    }
  }, [id, refreshState, status?.status, results?.length]);

  return (
    <>
      {id ? (
        <>
          {!status && <InvalidJobAlert />}
          {status?.status === "SUBMITTED" && <LoadingAlert params={params} />}
          {status?.status === "FAILED" && <FailureAlert status={status} />}
          {status?.status === "COMPLETED" && results?.length > 0 && <Results id={id} params={params} results={results} />}
        </>
      ) :
        <div>
          <p>Upload a VCF-formatted file, either plain text (.vcf) or <a href="https://www.htslib.org/doc/bgzip.html" target="_blank">bgzip</a> format (bgzip is installed with <a href="https://www.htslib.org/" target="_blank">Samtools</a>, or can be installed via conda or PyPi).  In general, you will need to have done some pre-filtering of the file since there is a default limit of 1000 rows in the output table. You can change the output to a smaller number if you wish.</p>
          <p>The output will be a table of variants along with various gene and protein IDs and the SIFT and PolyPhen scores from the VEP server. The mutant selected will be displayed at the top of the table in an embedded version of iCn3D.  You can interact with the mutation in the 3D viewer, as well as view 2D contacts and sequence annotations in the adjacent viewers.  When the mutation is loaded in the iCn3D viewer, you can <u>toggle between the wild-type and mutant amino acid by pressing the ‘a’ key on your keyboard</u>. You can open the mutation in a full version of iCn3D by clicking on the link and opening it in a new window.  See <a href="https://www.ncbi.nlm.nih.gov/Structure/icn3d/docs/icn3d_help.html" target="_blank">here</a> for more in-depth explanation of iCn3D, and <a href="https://www.ncbi.nlm.nih.gov/Structure/icn3d/icn3d.html#videos" target="_blank">here</a> for some tutorial videos.</p>
          <p>If you know what genes you are interested in, you can enter a comma-separated list of gene symbols, and only variants from those genes will be extracted from the VCF file.</p>
          <p>The program can take anywhere from under a minute for a handful of variants to 20 minutes or more for a large VCF file.  If you submit a large file you can click the button to have an email sent to you when the results are finished.</p>
        </div>
      }
    </>
  );
}

export function Results({ id, params, results }) {
  const [icn3dUrl, setIcn3dUrl] = useRecoilState(icn3dUrlState);
  const ref = useRef(null);

  useEffect(() => {
    const result = results.find(row => row?._mobileICn3Dlink?.includes("https"));
    setIcn3dUrl(result?._mobileICn3Dlink);

  }, [results, setIcn3dUrl]);

  useEffect(() => {
    ref?.current?.scrollIntoView();
  }, [icn3dUrl]);

  return (
    <>
      {icn3dUrl && <iframe ref={ref} width="100%" height="500vh" className="mb-1" title="ICN3D" src={icn3dUrl} />}
      <div className="d-flex flex-wrap justify-content-between">
        <div>
          Total # of Records: {results.length}
        </div>
        <a href={`${process.env.PUBLIC_URL}/api/data/input/${id}/${params.vcfFile}.gz_results.csv`} download>Download Results</a>
      </div>
      <VisualizeResultsTable results={results} />
    </>
  );
}

export function InvalidJobAlert() {
  return (
    <Alert variant="danger">
      <Alert.Heading className="mb-3">Invalid Job</Alert.Heading>
      <p>The job ID is invalid or has expired. Job results are valid for up to four weeks.</p>
    </Alert>
  );
}

export function LoadingAlert({ params = {} }) {
  return (
    <Alert variant="info">
      <Alert.Heading className="mb-3 d-flex align-items-center">
        Loading Results
        {!params.useAsync && (
          <Spinner animation="border" role="status" size="sm" className="mx-2">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        )}
      </Alert.Heading>
      <p>
        Your visualization request has been submitted.
        {params.useAsync && <span> You will receive an email once processing is complete.</span>}
      </p>
    </Alert>
  );
}

export function FailureAlert({ status = {} }) {
  const messages = [status?.error];
  if (status?.stdout) messages.push('\n', '[STDOUT]', status.stdout);
  if (status?.stderr) messages.push('\n', '[STDERR]', status.stderr);
  const message = messages.filter(Boolean).join("\n") || "INTERNAL ERROR";

  return (
    <Alert variant="danger">
      <Alert.Heading className="mb-3">Visualization Job Failed</Alert.Heading>
      <pre>{message}</pre>
    </Alert>
  );
}
